/**
 * These models define most of the top-level  Provider-related data models, including defining and running operations.
 */

import type { IncomingHttpHeaders } from 'node:http';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@timelyapp/integration-cloud-sdk/types/supabase';
import type Pusher from 'pusher';
import { z } from 'zod';
import { ProviderKey as ProviderKeyConst } from '../../provider-keys';
import {
  ConnectionSettingsSchema,
  SupportedSyncToTimelyEnumsSchema,
} from '../models.connection_settings';
import {
  GenericAccountId,
  GenericConnectionId,
  GenericHTTPHeadersSchema,
  GenericSubscriptionId,
  GenericToolId,
  GenericUserID,
  type ProviderLoggerInterface,
} from '../models.generic';
import { CreateProviderMemorySchema } from '../models.memories';
import type { TriggerPusherEventParams } from '../models.pusher';
import { IdentityReadSchema } from './integration.models.identity';
import {
  IntegrationScopeSchema,
  PublicIntegrationScopeSchema,
} from './integration.models.nango_auth';
import {
  AnyWriteResourceSchema,
  ResourceReadBaseSchema,
  type ResourceType,
  ResourceTypeEnums,
  ResourceTypeSchema,
} from './integration.models.resource';
import { ResourceSubscriptionBaseSchema } from './integration.models.resource.subscriptions';
import { WebhookReadSchema, WebhookUpdateSchema } from './integration.models.webhook';

export const ProviderKey = ProviderKeyConst;
export const ProviderIdSchema = z.enum(ProviderKey).describe('Provider identifier');
export type Providers = (typeof ProviderKey)[number];

// Backward compatibility - remove in Phase 3
export const ProviderKeySchema = ProviderIdSchema;

export const AllProviderDataTypesSchema = z.enum([...ResourceTypeEnums, 'identity']);
export type AllProviderDataTypes = z.infer<typeof AllProviderDataTypesSchema>;

/**
 * Sentinel value for connection-level operations that don't target a specific resource URL.
 * Used for operations like syncWebhookDefinitions and syncWebhooks that operate at the
 * connection level rather than on a specific API endpoint.
 *
 * This replaces the previous '/' sentinel which was not self-documenting.
 * Using z.literal() in the schema makes this explicit and prevents accidental misuse.
 *
 * @example
 * ```typescript
 * operationInstructions.push({
 *   storageOperation: null,
 *   operation: {
 *     id: 'syncWebhookDefinitions',
 *     params: {
 *       urlOrPath: '_connectionCallback',  // No specific resource URL
 *     },
 *   },
 * });
 * ```
 */

/**
 * Master operationId catalogue shared across integration-cloud and consumer SDKs.
 * Update this list whenever a provider introduces a new operation. Downstream schemas and the
 * taxonomy below derive their values from this tuple.
 */
export const KNOWN_OPERATION_IDS = [
  'importClients',
  'importClient',
  'importProjects',
  'importProject',
  'importProjectPermissions',
  'importTasks',
  'importTask',
  'importLabels',
  'importTeams',
  'importTeam',
  'importOrganizations',
  'importOrganization',
  'importIdentities',
  'importIdentity',
  'importPullRequests',
  'importPullRequest',
  'importMemories',
  'createProjects',
  'createTasks',
  'createMemories',
  'referenceTasks',
  'syncWebhookDefinitions',
  'syncWebhooks',
  'handleIncomingWebhook',
  'getCustomSettingsFields',
] as const;

export type KnownOperationId = (typeof KNOWN_OPERATION_IDS)[number];

// Historical name retained for backwards compatibility with generated schemas.
export const ProviderStorageOperationsIdsConst = [
  'importIdentityStorage',
  'importIdentitiesStorage',
  'syncWebhookDefinitionsStorage',
  'syncWebhooksStorage',
  'upsertResourcesAndAssociations',
  'upsertMemoriesAndAssociations',
  'importMemoriesStorage',
] as const;

const ProviderDataDiffOperationIdsConst = ['DataDiffCreated', 'DataDiffProcessed'] as const;
const ProviderDataDiffOperationIdsSchema = z.enum(ProviderDataDiffOperationIdsConst);

// ----------------------------------------------------------------------------
// Taxonomy metadata
// ----------------------------------------------------------------------------

export const OperationCategories = [
  'import',
  'create',
  'reference',
  'batch',
  'webhook',
  'query',
] as const;
export type OperationCategory = (typeof OperationCategories)[number];

export type OperationTaxonomyEntry = {
  category: OperationCategory;
  defaultTitle: string;
  timelyNoun?: string;
  resourceType?: ResourceType;
  /**
   * Controls whether the operation appears in user-facing feature copy (e.g. setup wizard import list).
   * `true` is reserved for the core import hierarchy (clients, projects, tasks, labels, teams, identities).
   * Any operation without this flag is treated as execution-only and hidden from summaries.
   */
  showInFeatureSummary?: boolean;
};

/**
 * Shared operation metadata used by both integration-cloud (fallback titles, execution hints) and
 * Timely frontend (feature summaries, taxonomy-aware copy). Consumers should prefer the provider's
 * explicit operation titles first, falling back to this table for canonical defaults.
 */
export const OPERATION_TAXONOMY = {
  importClients: {
    category: 'import',
    defaultTitle: 'Import Clients',
    timelyNoun: 'Clients',
    resourceType: 'client',
    showInFeatureSummary: true,
  },
  importClient: {
    category: 'import',
    defaultTitle: 'Import Client',
    timelyNoun: 'Client',
    resourceType: 'client',
  },
  importProjects: {
    category: 'import',
    defaultTitle: 'Import Projects',
    timelyNoun: 'Projects',
    resourceType: 'project',
    showInFeatureSummary: true,
  },
  importProject: {
    category: 'import',
    defaultTitle: 'Import Project',
    timelyNoun: 'Project',
    resourceType: 'project',
  },
  importProjectPermissions: {
    category: 'import',
    defaultTitle: 'Import Project Permissions',
    resourceType: 'project',
  },
  importTasks: {
    category: 'import',
    defaultTitle: 'Import Tasks',
    timelyNoun: 'Tasks',
    resourceType: 'task',
    showInFeatureSummary: true,
  },
  importTask: {
    category: 'import',
    defaultTitle: 'Import Task',
    timelyNoun: 'Task',
    resourceType: 'task',
    showInFeatureSummary: false,
  },
  importLabels: {
    category: 'import',
    defaultTitle: 'Import Labels',
    timelyNoun: 'Tags',
    resourceType: 'tag',
    showInFeatureSummary: true,
  },
  importTeams: {
    category: 'import',
    defaultTitle: 'Import Teams',
    timelyNoun: 'Teams',
    resourceType: 'team',
    showInFeatureSummary: true,
  },
  importTeam: {
    category: 'import',
    defaultTitle: 'Import Team',
    timelyNoun: 'Team',
    resourceType: 'team',
    showInFeatureSummary: false,
  },
  importOrganizations: {
    category: 'import',
    defaultTitle: 'Import Organizations',
    timelyNoun: 'Organizations',
    resourceType: 'organization',
    showInFeatureSummary: false,
  },
  importOrganization: {
    category: 'import',
    defaultTitle: 'Import Organization',
    timelyNoun: 'Organization',
    resourceType: 'organization',
    showInFeatureSummary: false,
  },
  importIdentities: {
    category: 'import',
    defaultTitle: 'Import Users',
    timelyNoun: 'Users',
    // ResourceType union does not include `identity`; leave undefined so UI treats this generically.
    showInFeatureSummary: true,
  },
  importIdentity: {
    category: 'import',
    defaultTitle: 'Import Identity',
    timelyNoun: 'User',
    showInFeatureSummary: false,
  },
  importPullRequests: {
    category: 'import',
    defaultTitle: 'Import Pull Requests',
    timelyNoun: 'Pull Requests',
    resourceType: 'pull_request',
    showInFeatureSummary: false,
  },
  importPullRequest: {
    category: 'import',
    defaultTitle: 'Import Pull Request',
    timelyNoun: 'Pull request',
    resourceType: 'pull_request',
    showInFeatureSummary: false,
  },
  importMemories: {
    category: 'import',
    defaultTitle: 'Import Memories',
    timelyNoun: 'Memories',
    showInFeatureSummary: false,
  },
  createProjects: {
    category: 'create',
    defaultTitle: 'Create Projects',
    timelyNoun: 'Projects',
    resourceType: 'project',
    showInFeatureSummary: false,
  },
  createTasks: {
    category: 'create',
    defaultTitle: 'Create Tasks',
    timelyNoun: 'Tasks',
    resourceType: 'task',
    showInFeatureSummary: false,
  },
  createMemories: {
    category: 'create',
    defaultTitle: 'Create Memories',
    timelyNoun: 'Memories',
    showInFeatureSummary: false,
  },
  referenceTasks: {
    category: 'reference',
    defaultTitle: 'Reference Tasks',
    timelyNoun: 'Tasks',
    resourceType: 'task',
    showInFeatureSummary: false,
  },
  syncWebhookDefinitions: {
    category: 'webhook',
    defaultTitle: 'Sync Webhook Definitions',
    showInFeatureSummary: false,
  },
  syncWebhooks: {
    category: 'webhook',
    defaultTitle: 'Sync Webhooks',
    showInFeatureSummary: false,
  },
  handleIncomingWebhook: {
    category: 'webhook',
    defaultTitle: 'Handle Incoming Webhook',
    showInFeatureSummary: false,
  },
  getCustomSettingsFields: {
    category: 'query',
    defaultTitle: 'Get Custom Settings Fields',
    showInFeatureSummary: false,
  },
} as const satisfies Record<KnownOperationId, OperationTaxonomyEntry>;

export type OperationTaxonomy = typeof OPERATION_TAXONOMY;

if (process.env.NODE_ENV !== 'production') {
  const missingTaxonomy = KNOWN_OPERATION_IDS.filter(id => !Object.hasOwn(OPERATION_TAXONOMY, id));
  if (missingTaxonomy.length) {
    throw new Error(`OPERATION_TAXONOMY is missing definitions for: ${missingTaxonomy.join(', ')}`);
  }
}

type CallableOperationId = {
  [K in KnownOperationId]: OperationTaxonomy[K]['category'] extends 'import' | 'webhook' | 'query'
    ? K
    : never;
}[KnownOperationId];

const providerCallableOperationIds = KNOWN_OPERATION_IDS.filter((id): id is CallableOperationId => {
  const category = OPERATION_TAXONOMY[id].category;
  return category === 'import' || category === 'webhook' || category === 'query';
}) as CallableOperationId[];

if (process.env.NODE_ENV !== 'production' && providerCallableOperationIds.length === 0) {
  throw new Error('No callable provider operations registered');
}

// Historical name retained for backwards compatibility with generated schemas.
// Consumers: BaseProvider.types.ts (BaseOperationIds) and api.integrations.ts (allowed operationIds).
export const ProviderImportOperationIdsConst = providerCallableOperationIds as unknown as readonly [
  CallableOperationId,
  ...CallableOperationId[],
];

export const ProviderBatchOperationIdsConst = ['initialize', 'synchronize'] as const;
export const ProviderBatchOperationIdsSchema = z.enum(ProviderBatchOperationIdsConst);
export type ProviderBatchOperationIds = z.infer<typeof ProviderBatchOperationIdsSchema>;

export const ProviderImportOperationsSchema = z.enum(ProviderImportOperationIdsConst).openapi({
  example: 'importOrganizations',
  description: 'The operationId that the provider supports',
});
/** All provider storage methods available  */
export const ProviderStorageOperationsSchema = z.enum(ProviderStorageOperationsIdsConst);
export type ProviderImportOperations = z.infer<typeof ProviderImportOperationsSchema>;

// Combined schema for all operations
export const ProviderOperationIdsSchema = z.union([
  ProviderImportOperationsSchema,
  ProviderStorageOperationsSchema,
  ProviderBatchOperationIdsSchema,
  z.literal('getConnectionDetails'), // Ref execute-provider-operation-ids & operationIdJobRuns
  z.literal('initAuthSession'),
  z.literal('forceFullImport'),
  z.literal('reset'), // Ref fn_connection_reset_or_delete
  ProviderDataDiffOperationIdsSchema, // Ref extractConnectionActivitiesFromTimelyDataDiff
]);

/**
 * `get-meta-about-operation-id.ts` defines standards metadata about each operationId
 */
export const ProviderOperationInstructionSchema = z.object({
  operationId: ProviderImportOperationsSchema,
  /** @todo/note: Consider making this an array aka support multiple callbacks. Probably useful for webhooks */
  operationStorageId: ProviderStorageOperationsSchema.nullable(),
  refreshConnectionDetailsAfterOperation: z.coerce.boolean().optional().default(false),
  injectToBatchOperationIdRuns: z.array(ProviderBatchOperationIdsSchema).optional(),
  /** These values are set automatically based on ^^ */
  runOnInitialize: z
    .boolean()
    .default(false)
    .optional()
    .openapi({ description: 'Run this operation after oAuth connection is successful' }),
  runOnSynchronize: z
    .boolean()
    .default(false)
    .optional()
    .openapi({ description: 'Run this operation when syncing the connection' }),
  providerScope: IntegrationScopeSchema.optional(),
});

export type ProviderOperationInstruction = z.infer<typeof ProviderOperationInstructionSchema>;

/**
 * Providers may require small dynamic input surfaces; define them using this JSONSchema-ish
 * definition so auth setup and connection settings can share the same rendering contract.
 */

const ProviderSetupFieldOptionValueSchema = z
  .custom<unknown>(value => value !== undefined, {
    message: 'Option value is required',
  })
  .describe('Value saved for this option by provider-generated UI');

export const ProviderSetupFieldOptionSchema = z.object({
  label: z.string().describe('Human-readable option label shown in provider-generated UI'),
  value: ProviderSetupFieldOptionValueSchema,
  description: z.string().optional().describe('Optional help text for this option'),
});

export type ProviderSetupFieldOption = z.infer<typeof ProviderSetupFieldOptionSchema>;

export const ProviderSetupFieldSchema = z.object({
  label: z
    .string()
    .describe('Human-readable field label shown in provider-generated UI')
    .openapi({ example: 'Enter your subdomain' }),
  fieldName: z
    .string()
    .describe('Stable provider field identifier in the active form context')
    .openapi({ example: 'subdomain' }),
  settingsFieldName: z
    .string()
    .optional()
    .describe(
      'Connection settings array/object key to receive this field value when different from fieldName'
    ),
  inputType: z
    .string()
    .optional()
    .describe('Frontend input type, e.g. text or select')
    .openapi({ example: 'text' }),
  defaultValue: z
    .union([z.string(), z.number(), z.boolean()])
    .nullable()
    .optional()
    .describe('Optional default value for the field')
    .openapi({
      example: 'd3v-dewocloud',
      description: 'Should only be used if we have a good idea of what the value will be.',
    }),
  description: z.string().optional().describe('Inline markdown-capable help text').openapi({
    example: 'Input the subdomain for your Zendesk account',
    description: 'Inline text description',
  }),
  config: z
    .object({
      pattern: z.string().describe('Regex pattern for string validation').openapi({
        description:
          'A RegExp object with the /g flag keeps track of the lastIndex where a match occurred.',
        example: '/[A-Za-z]{3}/',
      }),
      disabled: z.boolean().optional().describe('Whether the field is disabled').openapi({
        example: false,
      }),
      required: z.boolean().optional().describe('Whether a non-empty value is required'),
      minLength: z.number().optional().describe('Minimum string length').openapi({ example: 3 }),
      maxLength: z.number().optional().describe('Maximum string length').openapi({ example: 20 }),
    })
    .partial()
    .optional()
    .describe('Validation and display config for the setup field'),
  options: z
    .array(ProviderSetupFieldOptionSchema)
    .optional()
    .describe('Selectable options for select-like input types'),
});

export type ProviderSetupField = z.infer<typeof ProviderSetupFieldSchema>;

// Historical name retained for the existing provider auth setup contract.
export const ProviderFormFieldSchema = ProviderSetupFieldSchema;
export type ProviderFormField = ProviderSetupField;

export const ProviderImportProfileSchema = z.object({
  key: z
    .string()
    .min(1)
    .openapi({ example: 'default', description: 'Unique provider-scoped profile key' }),
  title: z.string().openapi({ example: 'Boards as Projects' }),
  description: z
    .string()
    .optional()
    .openapi({ example: 'Organizations → Boards (Projects) → Cards' }),
  isDefault: z.boolean().optional().openapi({
    example: true,
    description: 'Indicates the default profile selection when no key is provided',
  }),
  supportsCustomSettingsFields: z.boolean().optional().openapi({
    example: false,
    description:
      'Whether this import profile supports provider-defined connection settings candidates',
  }),
  resourceLabels: z
    .record(
      ResourceTypeSchema,
      z.object({
        singular: z.string(),
        plural: z.string(),
      })
    )
    .optional()
    .openapi({
      description:
        'Optional mapping of resource types to profile-aware display labels (e.g., project → list). Frontend templates can reference {provider}, {resourceType}, and {resourceTypePlural}.',
    }),
});

export type ProviderImportProfile = z.infer<typeof ProviderImportProfileSchema>;
export type ResourceLabelsByProfile = ProviderImportProfile['resourceLabels'];

export const ProviderOperationEnvironmentSchema = z.enum(['serverless', 'worker']);

export type ProviderOperationEnvironment = z.infer<typeof ProviderOperationEnvironmentSchema>;

/**
 * ProviderConfiguration / constructor params
 */

export const ProviderConfigurationSchema = z.object({
  connectionId: GenericConnectionId,
  actingUserId: GenericUserID,
  /** Base URL of API, e.g https://foo.bar.com/api/ */
  baseUrl: z.string().url().optional(), // ?? remove optional?
  /**
   * Additional base headers, extendable/overwritable by provider or at `this.fetch`-level
   * @default { 'Accept': 'application/json' }
   * @default { 'Content-Type': 'application/json' }
   * @default { 'User-Agent': 'TimelyIntegrationCloudAgent/1.0' }
   */
  baseHeaders: z.record(z.string(), z.string()).optional(),
  /**
   * What is the provider, e.g `github`
   * @note Relates to `tools.id` and Nango's "strategies"
   */
  provider: ProviderIdSchema,
  operationEnvironment: ProviderOperationEnvironmentSchema,
  /**
   * When true, allow the provider to operate on a disconnected connection for webhook cleanup.
   * Only intended for the disconnect/delete workflow.
   */
  disconnectedCleanup: z.boolean().optional(),
  /**
   * When true, forces last_synced_at bump on all storage operations.
   * Used in webhook mode to ensure resources are marked as recently synced.
   * @default false
   */
  isWebhookMode: z.boolean().optional(),
});

export type ProviderConfiguration = z.infer<typeof ProviderConfigurationSchema> & {
  /** Pass in an initiated/authenticated SupabaseClient */
  supabaseClient: SupabaseClient<Database>;
  /** ProviderLogger instance for branded, prefixed provider logs. */
  logger: ProviderLoggerInterface;
  /**
   * Optional admin client for dbLookup queries (bypasses RLS).
   * If not provided, BaseProvider creates one via `createSupabaseAdminClient()`.
   * Useful for testing: pass a stubbed client to avoid real DB connections.
   */
  supabaseAdminClient?: SupabaseClient<Database>;
};

/**
 * Base sync setting configuration schema - source of truth for operation settings
 * This defines the structure providers use to configure sync settings
 */
/**
 * Project sync option keys that are represented as boolean settings.
 *
 * @remarks
 * These keys are intentionally limited to boolean flags that can be expressed as
 * sync settings toggles. Non-boolean project configuration (e.g. `project_default_rate_type`)
 * should not be modeled as an operation setting key.
 */
export const SYNC_PROJECT_OPTION_SETTING_KEYS = [
  'project_add_all_workspace_users',
  'project_replace_users_on_update',
  'project_send_invites',
  'project_require_labels',
  'project_auto_add_assignees_to_projects',
] as const;

export const SyncProjectOptionSettingKeySchema = z.enum(SYNC_PROJECT_OPTION_SETTING_KEYS);

export const SyncSettingKeySchema = SupportedSyncToTimelyEnumsSchema.or(
  SyncProjectOptionSettingKeySchema
);
export type SyncSettingKey = z.infer<typeof SyncSettingKeySchema>;
export const SyncSettingConfigSchema = z.object({
  key: SyncSettingKeySchema,
  label: z.string().optional(), // Uses global default if not provided
  source: z.string().optional(), // Provider-specific description
  tooltip: z.string().optional(),
  dependant_on: z.array(SupportedSyncToTimelyEnumsSchema).optional(),
  defaultValue: z.boolean().optional(), // Default enabled state for this sync setting
  visibleToEndUser: z.coerce
    .boolean()
    .default(true)
    .optional()
    .describe('When false, setting is enforced but hidden from UI.'),
  internalOnly: z.coerce
    .boolean()
    .default(false)
    .optional()
    .describe('When true, setting is only visible to Timely employees.'),
});

/**
 * Base schema for individual operations in the provider's operationsInOrder array
 * This schema is used for API responses - settings are always arrays for consistency
 */
const BaseProviderOperationInOrderSchema = z.object({
  operationId: ProviderImportOperationsSchema.or(ProviderBatchOperationIdsSchema),
  title: z.string().optional(), // Auto-derived from operationId if not provided
  titleByProfile: z.record(z.string(), z.string()).optional().openapi({
    description: 'Optional mapping of profile key to operation title override.',
  }),
  provider_scope: IntegrationScopeSchema.optional().default('account'),
  run_on_initialize: z.boolean().default(false),
  run_on_synchronize: z.boolean().default(true),
  enabled: z.boolean().default(true).optional(), // Default enabled state for the operation
  supported: z.boolean().default(true).optional(), // Default supported state for the operation
  // Settings are always an array (or null) for consistent API responses
  settings: z.array(SyncSettingConfigSchema).nullable().optional(),
});

/**
 * Schema for individual operations in the provider's operationsInOrder array with validation
 */
export const ProviderOperationInOrderSchema = BaseProviderOperationInOrderSchema.superRefine(
  (data, ctx) => {
    // Validation: settings must be either null or fully specified with required fields
    if (data.settings !== null && data.settings !== undefined) {
      data.settings.forEach((setting, index) => {
        if (!setting.key) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Each setting must have a key field when provided',
            path: ['settings', index, 'key'],
          });
        }
      });
    }
  }
);

/**
 * Schema for extending ProviderOperationInOrderSchema (without validation)
 * Use this for extending the schema in other places
 */
export const ExtendableProviderOperationInOrderSchema = BaseProviderOperationInOrderSchema;

export type ProviderOperationInOrder = z.infer<typeof ProviderOperationInOrderSchema>;
export type SyncSettingConfig = z.infer<typeof SyncSettingConfigSchema>;

/**
 * Flattened schema for defining the provider - combines all provider metadata and capabilities
 */
export const ProviderInfoSchema = z.object({
  // Provider metadata (previously in ProviderAboutSchema)
  title: z.string().openapi({ example: 'Zendesk' }),
  description: z.string().optional().openapi({ example: 'Integrate with Zendesk to do XY & Z' }),
  icon_url: z.string().optional().openapi({ example: 'https://example.com/icon.png' }),
  url_homepage: z.string().optional().openapi({ example: 'https://example.com' }),
  url_support: z.string().optional().openapi({ example: 'https://example.com/support' }),
  /**
   * Providers with max 1 root resource have access to `this.connectionDetails.root_resource`
   * If >1 root resource can exist, you can still find all root resources via `this.connectionDetails.resources[].root_uri === null`
   *
   * This is helpful for constructing URI's when importing sub/child resources many levels deep.
   */
  singleRootResource: z.boolean().openapi({ example: true }),
  supportsPersonalConnection: z.boolean().optional().openapi({ example: true }),
  supportsAccountConnection: z.boolean().optional().openapi({ example: true }),
  /**
   * Webhook scope support - defines which connection scopes can use webhooks
   * Used for connection settings validation and BaseProvider webhook logic
   */
  supportsPersonalWebhooks: z.boolean().default(false).openapi({
    example: false,
    description: 'Whether personal connections can use webhooks for real-time sync',
  }),
  supportsAccountWebhooks: z.boolean().default(true).openapi({
    example: true,
    description: 'Whether account connections can use webhooks for real-time sync',
  }),
  // Provider capabilities and configuration
  operationsInOrder: z.array(ProviderOperationInOrderSchema),
  /**
   * Sync settings declared independently of operations. Fan-out framework providers
   * declare their settings here; legacy `BaseProvider` classes may still declare them
   * inline on `operationsInOrder[].settings`. The integration-details query resolves a
   * single flat list from whichever the provider populated (preferring this field), so
   * the frontend reads one provider-plane-agnostic list. Account-scoped — settings are
   * not filtered by connection scope.
   */
  settings: z.array(SyncSettingConfigSchema).optional(),
  oAuthScopes: z.array(
    z.object({
      key: z.string().openapi({ example: 'read:org', description: 'The actual oAuth scope' }),
      providerScope: IntegrationScopeSchema,
      description: z.string().openapi({ example: 'Read-only access to your organizations' }),
    })
  ),
  formFields: z.array(ProviderFormFieldSchema),
  importProfiles: z.array(ProviderImportProfileSchema).optional(),
});

export type ProviderInfo = z.infer<typeof ProviderInfoSchema>;

/**
 * Holds metadata about the XHR operation, e.g, rate limits, cursors, etc,
 * which are useful to know about when the next import/sync happens,
 * e.g to avoid fetching pages of data we already have.
 */
const XhrOperationMetaSchema = z.object({
  when: z.string().datetime({ offset: true }).optional(),
  url: z.string().url(),
  lastRecordId: z.string().or(z.number()).optional().nullable(),
  firstRecordId: z.string().or(z.number()).optional().nullable(),
  firstFewRecordsFingerprint: z.string().or(z.number()).optional().nullable(),
  status: z.number().optional(),
  lastModified: z.string().datetime({ offset: true }).optional(),
  /** @deprecated */
  eTag: z.string().optional().nullable(),
  eTags: z.array(z.string()).optional().nullable(),
  cursor: z.string().or(z.number()).optional().nullable(),
  rateLimit: z.number().or(z.string()).optional().nullable(),
  rateLimitRemaining: z.number().or(z.string()).optional().nullable(),
  nextUrl: z.string().url().optional(),
});
export type XhrOperationMeta = z.infer<typeof XhrOperationMetaSchema>;

export const RateLimitHeaderMappingSchema = z.object({
  /** Header for total allowed in window */
  limit: z.string().optional(),
  /** Header for remaining count */
  remaining: z.string().optional(),
  /** Header for reset time */
  reset: z.string().optional(),
  /** Header for retry-after (429 only) */
  retryAfter: z.string().optional(),
});
export type RateLimitHeaderMapping = z.infer<typeof RateLimitHeaderMappingSchema>;

export const ProviderRateLimitConfigSchema = z.object({
  /** Target req/sec (~80% of provider limit) */
  requestsPerSecond: z.number().positive(),
  /** Custom header mapping; auto-detects if omitted */
  headerMapping: RateLimitHeaderMappingSchema.optional(),
  /** Slow down when quota low */
  adaptivePacing: z.boolean().default(true),
  /** Delay multiplier at <20% remaining */
  exhaustionMultiplier: z.number().positive().default(2.0),
  minDelayMs: z.number().nonnegative().default(50),
  maxDelayMs: z.number().positive().default(5000),
});
export type ProviderRateLimitConfig = z.infer<typeof ProviderRateLimitConfigSchema>;

export const ParsedRateLimitInfoSchema = z.object({
  /** Total allowed in window */
  limit: z.number().nullable(),
  /** Remaining in window */
  remaining: z.number().nullable(),
  /** Unix timestamp when resets (x-ratelimit style) */
  resetAt: z.number().nullable(),
  /** Seconds until reset (IETF draft) */
  resetInSeconds: z.number().nullable(),
  /** From Retry-After header */
  retryAfterSeconds: z.number().nullable(),
});
export type ParsedRateLimitInfo = z.infer<typeof ParsedRateLimitInfoSchema>;

const XhrLogSchema = z.object({
  operationId: ProviderImportOperationsSchema,
  subscriptionId: GenericSubscriptionId.optional(),
  lastXhr: XhrOperationMetaSchema,
});
export type XhrLog = z.infer<typeof XhrLogSchema>;

/**
 * Metadata on `connection` or `resource_subscription` describes
 * the last operation runs, errors, and other useful information.
 */
export const ConnectionOrSubscriptionMetaPropertiesSchema = z.object({
  when: z.string().datetime({ offset: true }).optional(),
  error: z.string().or(z.boolean()).optional().nullable(),
  executionSeconds: z.coerce.number().optional().nullable(),
  lastXhr: XhrOperationMetaSchema.optional(),
  operationId: ProviderImportOperationsSchema.optional(),
});

export type ConnectionOrSubscriptionMeta = z.infer<typeof ConnectionOrSubscriptionMetaSchema>;
export const ConnectionOrSubscriptionMetaSchema = z.record(
  ProviderImportOperationsSchema,
  ConnectionOrSubscriptionMetaPropertiesSchema
);

const ProviderConnectionSubscriptionResourceSchema = ResourceReadBaseSchema.pick({
  id: true,
  uri: true,
  // type: true, // Zod now complains of unknown keys, keeping as reference
  external_id: true,
  // crupdated_payload: true, // Zod now complains of unknown keys, keeping as reference
  url_api: true,
  url_html: true,
  metadata: true,
}).extend({
  type: ResourceTypeSchema,
  // Root & Base Resources have different understanding of `root_uri` and `parent_uri`
  // So adding here as optional to avoid excessive type checking in Provider implementations
  root_uri: z.string().url().nullable().optional(),
  parent_uri: z.string().url().nullable().optional(),
  // Include timestamp fields needed for webhook cleanup logic
  deleted_at: z.string().datetime({ offset: true }).nullable().optional().default(null),
  archived_at: z.string().datetime({ offset: true }).nullable().optional().default(null),
});

export type ProviderConnectionSubscriptionResource = z.infer<
  typeof ProviderConnectionSubscriptionResourceSchema
>;

/**
 * General information abhout a subscription, the resource it's for, and any webhooks installed.
 */
export const ProviderConnectionDetailsSubscriptionSchema = ResourceSubscriptionBaseSchema.pick({
  id: true,
  synced_at: true,
  // external_id: true, // Zod now complains of unknown keys, keeping as reference
  enabled: true,
  supports_webhooks: true,
}).extend({
  resource: ProviderConnectionSubscriptionResourceSchema,
  webhooks: z.array(WebhookReadSchema),
  metadata: ConnectionOrSubscriptionMetaSchema.optional(),
  connection: z.object({
    id: GenericConnectionId,
    managing_user_id: GenericUserID,
  }),
});

export type ProviderConnectionDetailsSubscription = z.infer<
  typeof ProviderConnectionDetailsSubscriptionSchema
>;

/**
 * BaseProvider makes this schema available to all providers via `this.connectionDetails`
 * Token is now handled lazily via getToken() method instead of being stored in connectionDetails
 */
export const ProviderConnectionDetailsSchema = z.object({
  connectionId: GenericConnectionId,
  nangoConnectionId: z.string().nullable(),
  providerKey: z.string(),
  actingUserId: GenericUserID,
  scope: PublicIntegrationScopeSchema,
  accountId: GenericAccountId,
  toolId: GenericToolId,
  identity: IdentityReadSchema.nullable(), // always null on initial connection
  metadata: ConnectionOrSubscriptionMetaSchema,
  config: z
    .object({
      subdomain: z.string().optional().nullable(),
      instance_url: z.string().optional().nullable(),
    })
    .nullable(),
  /** All subscriptions on this connection */
  subscriptions: z.array(ProviderConnectionDetailsSubscriptionSchema),
  settings: ConnectionSettingsSchema,
  disconnected_at: z.string().datetime({ offset: true }).nullable().optional(),
});

export type ProviderConnectionDetails = z.infer<typeof ProviderConnectionDetailsSchema>;

export const ExtendedFetchOptionsSchema = z.object({
  operationId: ProviderImportOperationsSchema,
  subscriptionId: GenericSubscriptionId.optional(),
  /** The current subscription (with `resource`) from `this.connectionDetails.subscriptions[] */
  subscription: ProviderConnectionDetailsSubscriptionSchema.optional(),
  /** The root resource for this subscription, e.g, the organization or project */
  root_resource: ProviderConnectionSubscriptionResourceSchema.optional(),

  debug: z.boolean().default(false).optional(),
  /**
   * Pass along the last XHR operation meta, so we can make smart decisions
   * on what XHR operations to run (e.g, avoid paginating past xhr.lastRecordId)
   */
  previousXhr: XhrOperationMetaSchema.optional(),
  /**
   * How many pages should we fetch? Defaults to 30.
   */
  maxPages: z.number().default(10).optional(),
  /**
   * Follow known links to "next page" automatically (links header, `next_page` and similar in response)
   * Defaults to `true`.
   */
  autoFollowNextPage: z.boolean().default(true).optional(),
  /**
   * First 3 records on page 1 are fingerprinted ("id1-id2-id3")
   * If we see the same 3 records next time we run an import, we'll abort.
   * @default true
   * @warning Turn off if API endpoint is not sorted by most recently updated.
   */
  abortOnIdFingerprintMatch: z.coerce.boolean().default(true).optional(),
  /**
   * Some API's return the data on nested objects, ala { data: [ ... ]}
   * Smartness around `fetch` require a flat array of data, so we need to unwrap it.
   * If we at some point miss out on data we need, returning that via `meta` is probably ideal.
   */
  unwrapEnvelopeKey: z.string().optional(),

  /** If the response has a non-standard id field, we need to specify it for pagination logic to work */
  idField: z.string().optional(),

  /**
   * Some API's return 404 if there are no results instead of returning an empty result like
   * a sane API would.
   * Allow specifying responses that should count as "return empty response"
   */
  emptyHttpResponseCodes: z.array(z.number()).optional(),

  /** Retry attempt index for telemetry purposes. */
  retryAttempt: z.number().int().nonnegative().optional(),
  /** Maximum retries configured for the request. */
  retryLimit: z.number().int().positive().optional(),

  /**
   * Provider rate limit configuration for pacing and retry behavior.
   * Passed from BaseProvider.rateLimitConfig for provider-aware throttling.
   */
  rateLimitConfig: ProviderRateLimitConfigSchema.optional(),
  /**
   * Last known rate limit state from a previous response.
   * Used for adaptive pacing between paginated requests.
   */
  lastRateLimitInfo: ParsedRateLimitInfoSchema.nullable().optional(),

  /**
   * Per-request timeout in ms, covering time-to-headers. When it fires, the
   * request is aborted with a `TimeoutError` that the retry layer classifies as
   * retryable, so a call wedged before headers arrive rides the retry budget
   * instead of hanging on undici's ~300s header timeout. The body read is not
   * under this timeout (undici's ~300s body timeout bounds it), but a network
   * failure during it rides the same retry budget. Defaults to
   * `DEFAULT_REQUEST_TIMEOUT_MS` (see `fetch-internals`), matching the framework
   * fetcher's 30s default. Overridable per-provider.
   */
  requestTimeoutMs: z.number().positive().optional(),

  /**
   * Page index starting value: 0 or 1. Default 1 for most APIs.
   * Use 0 for APIs like ClickUp that use 0-indexed pagination (page=0,1,2...).
   */
  pageIndexStart: z
    .union([z.literal(0), z.literal(1)])
    .default(1)
    .optional(),

  /**
   * Response field path (lodash get syntax) for "is this the last page" boolean indicator.
   * When the value at this path is true (or matches lastPageIndicatorValue), pagination stops.
   * @example 'last_page' for ClickUp's { tasks: [...], last_page: true }
   * @example 'pagination.is_last' for nested indicators
   */
  lastPageIndicatorPath: z.string().optional(),

  /**
   * The value that indicates "this is the last page" when found at lastPageIndicatorPath.
   * - For 'last_page' style APIs: use default (true) - stop when last_page: true
   * - For 'has_more' style APIs: use false - stop when has_more: false
   * @default true
   */
  lastPageIndicatorValue: z.boolean().default(true).optional(),
});

/**
 * Mutates url and headers for a single outbound request. Called by the fetch layer
 * immediately before each HTTP request (including every page of auto-pagination and
 * every retry attempt), so per-request state like a freshly-refreshed Bearer token
 * or OAuth1 signature can be injected just-in-time.
 */
export type RequestPreparer = (url: URL, headers: Record<string, string>) => void | Promise<void>;

export type ExtendedFetchOptions = z.infer<typeof ExtendedFetchOptionsSchema> &
  RequestInit & {
    /** ProviderLogger instance for branded, prefixed fetch logs. */
    logger?: ProviderLoggerInterface;
    /** Hook invoked by the fetch layer to prepare url and headers per request. */
    prepareRequest?: RequestPreparer;
    /** Hook invoked by the fetch layer to force a fresh provider token before a reactive auth retry. */
    forceTokenRefresh?: () => Promise<void>;
  };

export const UnifiedFetchForSubscriptionResourceOptionsSchema = ExtendedFetchOptionsSchema.pick({
  autoFollowNextPage: true,
  maxPages: true,
  unwrapEnvelopeKey: true,
  idField: true,
  operationId: true,
  subscription: true,
  root_resource: true,
  subscriptionId: true,
  abortOnIdFingerprintMatch: true,
  emptyHttpResponseCodes: true,
  retryAttempt: true,
  retryLimit: true,
}).extend({
  /**
   * The type of `resource.type` we want to import from.
   * @see `resourceId` for a specific `resource.id` we want to import from.
   * @example 'organization' or 'project'
   */
  resourceType: ResourceTypeSchema.optional(),
  /**
   * A specific `resource.id` we want to import from.
   * @see `resourceType` for a specific `resource.type` we want to import from.
   */
  resourceId: z.string().uuid().optional(),
  /**
   * A specific `resource.uri` we want to import from.
   */
  resourceUri: z.string().url().optional(),
  /**
   * URL or path to fetch.
   * Supports `resource`'s placeholders: {resource_external_id}, {resource_external_code}, {resource_external_type}, {resource_api_url}
   * See `injectUrlPlaceholders` for more details.
   *
   * For connection-level operations that don't target a specific resource URL,
   * use the sentinel value '_connectionCallback'.
   *
   * @example Github: `/repos/Timely/app/issues` (postfixed to baseUrl)
   * @example Github: `/repos/{resource_external_code}/issues` (postfixed to baseUrl)
   * @example Github: `{resource_api_url}/issues` (full URL)
   * @example Asana: `/projects?workspace={resource_external_id}` (postfixed to baseUrl)
   * @example Connection-level: `'_connectionCallback'` (for syncWebhooks, syncWebhookDefinitions)
   */
  urlOrPath: z.union([z.literal('_connectionCallback'), z.string().min(1)]),

  /** An operation will automatically default to the   */
  baseUrl: z.string().url().optional(),

  /**
   * For graphQL queries
   */

  /** GraphQL query to execute */
  query: z.string().optional(),
  /** Variables/params to pass to the query */
  variables: z.record(z.string(), z.any()).optional(),
  /** Path to the data in the response. eg. 'projects.nodes'  */
  dataPath: z.string().optional(),
  /** Path to the pagination info in the response */
  paginationPath: z.string().optional(),

  /**
   * Pagination style for GraphQL APIs.
   * - 'relay' (default): Uses `first/after` variables, expects `pageInfo.hasNextPage/endCursor`
   * - 'cursor': Uses configurable `limit/cursor` variables, cursor directly in response
   */
  paginationStyle: z.enum(['relay', 'cursor']).optional(),
  /**
   * Path to the cursor in the response (for 'cursor' pagination style).
   * @example 'boards.0.items_page.cursor' for Monday.com
   */
  cursorPath: z.string().optional(),
  /**
   * Name of the cursor variable in the GraphQL query (for 'cursor' pagination style).
   * @default 'cursor'
   */
  cursorVariableName: z.string().optional(),
  /**
   * Name of the limit/page-size variable in the GraphQL query (for 'cursor' pagination style).
   * @default 'limit'
   */
  limitVariableName: z.string().optional(),
});

/** Generic params that certain operation's accept */
export type FetchForSubscriptionResourceOptions = z.infer<
  typeof UnifiedFetchForSubscriptionResourceOptionsSchema
>;
/**
 * Params for calling an operationId
 */
export const OperationIdParamsSchema = UnifiedFetchForSubscriptionResourceOptionsSchema.pick({
  urlOrPath: true,
  resourceUri: true,
}).extend({
  /**
   * Organization ID extracted from webhook payload context.
   * When a board is removed from organization or deleted,
   * Trello API returns board.idOrganization = null, but webhook payload still
   * contains the organization ID. This field preserves parent_uri relationship
   * for deleted/archived boards, preventing them from becoming orphaned top-level resources.
   */
  _webhook_organization_id: z.string().optional(),

  /**
   * Parent resource URI for scoped operations.
   * When provided, operation fetches children of this parent only instead of full subscription import.
   * Used for webhook cascade imports (e.g., import projects under a specific client).
   * @example 'clickup://space_list/client/123' for importing lists under a specific space
   */
  parentUri: z.string().optional(),

  /**
   * External ID of parent resource (alternative to parentUri when URI not yet available).
   * Requires parentType to be specified for URI construction.
   * @example '123' for ClickUp space/folder ID
   */
  parentExternalId: z.string().optional(),

  /**
   * Type of parent resource when using parentExternalId.
   * Used with parentExternalId to construct the parent URI.
   * @example 'client' | 'project' | 'organization'
   */
  parentType: ResourceTypeSchema.optional(),

  /**
   * If true, the operation should generate a Memory event after processing.
   * Used by webhook handlers to trigger memory creation for task/project events.
   */
  generateMemory: z.boolean().optional(),
  /**
   * Webhook event context for memory generation.
   * Contains action, user, timestamp, and resource info from the original webhook.
   */
  webhookContext: z.record(z.unknown()).optional(),
});

export type OperationIdParams = z.infer<typeof OperationIdParamsSchema>;

/**
 * Params for calling `handleIncomingWebhook`
 * The entire NextApiRequest object is also passed along (`headers` and `body` explicitly defined, but everything's available)
 */
export const WebhookOperationParamsSchema = z.object({
  headers: GenericHTTPHeadersSchema,
  body: z.any(),
  subscription: ProviderConnectionDetailsSubscriptionSchema,
  /**
   * Raw request body as a string, used for signature verification (HMAC).
   * Providers like Asana and Linear require the exact raw body for signature computation.
   */
  rawBody: z.string().optional(),
});

/**  */
export type WebhookOperationParams = z.infer<typeof WebhookOperationParamsSchema> & {
  headers: IncomingHttpHeaders;
  body: any;
};

const OperationInstructionStorageSchema = z.union([
  z.object({
    id: z.literal('upsertResourcesAndAssociations'),
    resources: z.array(AnyWriteResourceSchema),
  }),
  z.object({
    id: z.literal('upsertMemoriesAndAssociations'),
    memories: z.array(CreateProviderMemorySchema),
  }),
]);

/**
 * Unified operation instruction schema for both provider operations and webhook reactions
 * Consolidated from ProviderOperationInstructionSchema and old OperationInstructionSchema
 */
export const OperationInstructionSchema = z.object({
  /** What _import_ operationId to run in response to the webhook hit. */
  operation: z
    .object({
      id: ProviderImportOperationsSchema,
      params: OperationIdParamsSchema,
    })
    .nullable(),
  /**
   * What _storage_ operationId to run in response to the webhook hit.
   * @note This is useful when the webhook payload has enough data to construct the full resource.
   */
  storageOperation: OperationInstructionStorageSchema.nullable(),
});

export type OperationInstruction = z.infer<typeof OperationInstructionSchema>;

export const IncomingWebhookHitReactionSchema = z.object({
  operationInstructions: z.array(OperationInstructionSchema),
  /** Should we respond to webhook hit origin with a status/payload/headers? */
  responseToOrigin: z
    .object({
      statusCode: z.number().default(200).optional(),
      /**
       * Optional response headers to include in the HTTP response.
       * Required for providers like Asana that need headers echoed during webhook handshake.
       * @example { 'X-Hook-Secret': 'secret-from-request' }
       */
      headers: z.record(z.string()).optional(),
      body: z.object({}).passthrough().nullable(),
    })
    .nullable(),
  updateSubscribedWebhook: WebhookUpdateSchema.nullable(),
});

export type IncomingWebhookHitReaction = z.infer<typeof IncomingWebhookHitReactionSchema>;

/**
 * "Public" API for calling operations on a provider
 * @todo Stable API endpoint
 * @todo Job runner implementation that accepts same input
 */
export const ExecuteProviderOperationIdsParamsSchema = z.object({
  provider: ProviderIdSchema,
  connectionId: GenericConnectionId,
  actingUserId: GenericUserID,
  operationEnvironment: ProviderOperationEnvironmentSchema,
  /** If no operationIds are supplied, the provider's `initializingOperationIdsInOrder` will run  */
  operationIds: z.array(ProviderImportOperationsSchema).optional(),
  /** Run a predefined set of operations, see `initialize[Personal|Account]OperationIds` and `synchronize[Personal | Account]OperationIds` */
  operationBatchPreset: ProviderBatchOperationIdsSchema.optional(),
  /** Params to pass to each operation */
  operationParams: OperationIdParamsSchema.optional(),
  /** Params passed to `handleIncomingWebhooks` operation (upon webhook hit) */
  webhookOperationParams: WebhookOperationParamsSchema.optional(),
  /**
   * Force isWebhookMode=true for provider configuration (triggers param_force_last_synced in SQL).
   * Used for follow-up operations triggered by webhooks where webhookOperationParams isn't passed.
   * When not provided, falls back to !!webhookOperationParams.
   */
  isWebhookMode: z.boolean().optional(),
  disconnectedCleanup: z.boolean().optional(),
});

export type ExecuteProviderOperationIdsParams = z.infer<
  typeof ExecuteProviderOperationIdsParamsSchema
> & {
  supabaseClient: SupabaseClient<Database>;
  /**
   * Optional admin client for dbLookup queries (bypasses RLS).
   * If not provided, BaseProvider creates one via `createSupabaseAdminClient()`.
   * Useful for testing: pass a stubbed client to avoid real DB connections.
   */
  supabaseAdminClient?: SupabaseClient<Database>;
  /** Optional job ID for tracking. If not provided, one will be auto-generated. */
  jobId?: string;
  /** Optional Pusher event dispatch configuration. If provided, the function will dispatch job events. */
  pusher?: Omit<TriggerPusherEventParams<'integration_job_event'>, 'event' | 'payload'> & {
    timelyAccountId: number;
  };
  /** Optional override for the Pusher client (useful for Trigger workers). */
  pusherClient?: Pusher;
};
